import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportOptions = ({ syncResult, onExport }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');

  const exportOptions = [
    {
      id: 'detailed-log',
      title: 'Detailed Sync Log',
      description: 'Complete log with timestamps, match scores, and error details',
      icon: 'FileText',
      formats: ['txt', 'json']
    },
    {
      id: 'match-report',
      title: 'Match Report',
      description: 'Song matching results with confidence scores',
      icon: 'BarChart3',
      formats: ['csv', 'xlsx']
    },
    {
      id: 'failed-songs',
      title: 'Failed Songs List',
      description: 'List of songs that could not be matched',
      icon: 'AlertCircle',
      formats: ['csv', 'txt']
    },
    {
      id: 'playlist-backup',
      title: 'Playlist Backup',
      description: 'Original playlist data for backup purposes',
      icon: 'Archive',
      formats: ['json', 'm3u']
    }
  ];

  const handleExport = async (optionId, format) => {
    setIsExporting(true);
    try {
      await onExport(optionId, format);
    } finally {
      setIsExporting(false);
    }
  };

  const generateFileName = (optionId, format) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const playlistName = syncResult.playlistName.replace(/[^a-zA-Z0-9]/g, '_');
    return `${playlistName}_${optionId}_${timestamp}.${format}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">Export Options</h3>
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="Download" size={16} />
          <span>Choose format and download</span>
        </div>
      </div>

      <div className="grid gap-4">
        {exportOptions.map((option) => (
          <div key={option.id} className="p-4 bg-surface border border-border rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 p-2 bg-primary/10 rounded-lg">
                <Icon name={option.icon} size={20} className="text-primary" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-text-primary mb-1">
                  {option.title}
                </h4>
                <p className="text-sm text-text-secondary mb-3">
                  {option.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {option.formats.map((format) => (
                    <Button
                      key={format}
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconSize={14}
                      onClick={() => handleExport(option.id, format)}
                      disabled={isExporting}
                      className="text-xs"
                    >
                      {format.toUpperCase()}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Export Section */}
      <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-center space-x-3 mb-3">
          <Icon name="Zap" size={20} className="text-primary" />
          <h4 className="text-sm font-medium text-text-primary">Quick Export</h4>
        </div>
        
        <p className="text-sm text-text-secondary mb-4">
          Export all sync data in a single comprehensive report
        </p>
        
        <div className="flex items-center space-x-3">
          <select
            value={exportFormat}
            onChange={(e) => setExportFormat(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="csv">CSV Format</option>
            <option value="json">JSON Format</option>
            <option value="xlsx">Excel Format</option>
          </select>
          
          <Button
            variant="primary"
            iconName="Download"
            iconSize={16}
            onClick={() => handleExport('complete-report', exportFormat)}
            disabled={isExporting}
            loading={isExporting}
          >
            Export All Data
          </Button>
        </div>
      </div>

      {/* Export Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
        <div className="p-3 bg-surface rounded-lg">
          <p className="text-lg font-bold text-success">{syncResult.successCount}</p>
          <p className="text-xs text-text-muted">Successful Matches</p>
        </div>
        <div className="p-3 bg-surface rounded-lg">
          <p className="text-lg font-bold text-error">{syncResult.failedCount}</p>
          <p className="text-xs text-text-muted">Failed Matches</p>
        </div>
        <div className="p-3 bg-surface rounded-lg">
          <p className="text-lg font-bold text-warning">{syncResult.duplicatesCount}</p>
          <p className="text-xs text-text-muted">Duplicates Found</p>
        </div>
        <div className="p-3 bg-surface rounded-lg">
          <p className="text-lg font-bold text-text-primary">{syncResult.totalTracks}</p>
          <p className="text-xs text-text-muted">Total Processed</p>
        </div>
      </div>
    </div>
  );
};

export default ExportOptions;